import type { Agent, AgentOutputType } from '../agent';
import { UserError } from '../errors';
import { type RunItem, takeAgentInputFromPool } from '../items';
import type { ModelResponse } from '../model';
import type { RunContext } from '../run-context';
import { addErrorToCurrentSpan } from '../tracing/context';
import type { AgentInputItem } from '../types';
import {
  buildAgentInputPool,
  extractOutputItemsFromRunItems,
  getAgentInputItemKey,
  type ReasoningItemIdPolicy,
  removeAgentInputFromPool,
  toAgentInputList,
} from './items';
import type { CallModelInputFilter, CallModelInputFilterArgs } from './types';

/**
 * Tracks which items have already been sent to or received from the Responses API when the caller
 * supplies `conversationId`/`previousResponseId`. This ensures we only send the delta each turn.
 */
export class ServerConversationTracker {
  public conversationId?: string;
  public previousResponseId?: string;
  private readonly reasoningItemIdPolicy?: ReasoningItemIdPolicy;

  // Using this flag because WeakSet does not provide a way to check its size.
  private sentInitialInput = false;
  // The items already sent to the model; using WeakSet for memory efficiency.
  private sentItems = new WeakSet<object>();
  // The items received from the server; using WeakSet for memory efficiency.
  private serverItems = new WeakSet<object>();
  // Semantic keys for server items. Generated SDK items get local UUIDs, so object identity or
  // raw JSON equality alone cannot identify server-acknowledged items after normalization.
  private serverItemKeys = new Set<string>();
  // Tracks which prepared turn-input item originated from which source object.
  private preparedItemSources = new WeakMap<object, AgentInputItem>();
  // Track initial input items that have not yet been sent so they can be retried on later turns.
  private remainingInitialInput: AgentInputItem[] | null = null;

  constructor({
    conversationId,
    previousResponseId,
    reasoningItemIdPolicy,
  }: {
    conversationId?: string;
    previousResponseId?: string;
    reasoningItemIdPolicy?: ReasoningItemIdPolicy;
  }) {
    this.conversationId = conversationId ?? undefined;
    this.previousResponseId = previousResponseId ?? undefined;
    this.reasoningItemIdPolicy = reasoningItemIdPolicy;
  }

  /**
   * Pre-populates tracker caches from an existing RunState when resuming server-managed runs.
   */
  primeFromState({
    originalInput,
    generatedItems,
    modelResponses,
  }: {
    originalInput: string | AgentInputItem[];
    generatedItems: RunItem[];
    modelResponses: ModelResponse[];
  }) {
    if (this.sentInitialInput) {
      return;
    }

    const originalItems = toAgentInputList(originalInput);
    const hasResponses = modelResponses.length > 0;

    for (const response of modelResponses) {
      for (const item of response.output) {
        if (item && typeof item === 'object') {
          this.trackServerItem(item as AgentInputItem);
        }
      }
    }

    if (hasResponses) {
      for (const item of originalItems) {
        if (item && typeof item === 'object') {
          this.sentItems.add(item);
        }
      }

      this.sentInitialInput = true;
      this.remainingInitialInput = null;
    }

    const latestResponse = modelResponses[modelResponses.length - 1];
    if (!this.conversationId && latestResponse?.responseId) {
      this.previousResponseId = latestResponse.responseId;
    }

    if (hasResponses) {
      for (const item of generatedItems) {
        const rawItem = item.rawItem;
        if (!rawItem || typeof rawItem !== 'object') {
          continue;
        }
        if (this.isServerAcknowledgedItem(rawItem as AgentInputItem)) {
          this.sentItems.add(rawItem);
        }
      }
    }
  }

  /**
   * Records the raw items returned by the server so future delta calculations skip them.
   * Also captures the latest response identifier to chain follow-up calls when possible.
   */
  trackServerItems(modelResponse: ModelResponse | undefined) {
    if (!modelResponse) {
      return;
    }
    for (const item of modelResponse.output) {
      if (item && typeof item === 'object') {
        this.trackServerItem(item as AgentInputItem);
      }
    }
    if (!this.conversationId && modelResponse.responseId) {
      this.previousResponseId = modelResponse.responseId;
    }
  }

  /**
   * Returns the minimum set of items that still need to be delivered to the server for the
   * current turn. This includes the original turn inputs (until acknowledged) plus any
   * newly generated items that have not yet been echoed back by the API.
   */
  prepareInput(
    originalInput: string | AgentInputItem[],
    generatedItems: RunItem[],
    supplementalGeneratedItems: AgentInputItem[] = [],
  ): AgentInputItem[] {
    const inputItems: AgentInputItem[] = [];
    const generatedItemsForInput: RunItem[] = [];

    if (!this.sentInitialInput) {
      const initialItems = toAgentInputList(originalInput);
      // Preserve the full initial payload so a filter can drop items without losing their originals.
      inputItems.push(...initialItems);
      for (const item of initialItems) {
        this.registerPreparedItemSource(item);
      }
      this.remainingInitialInput = initialItems.filter(
        (item): item is AgentInputItem =>
          Boolean(item) && typeof item === 'object',
      );
      this.sentInitialInput = true;
    } else if (
      this.remainingInitialInput &&
      this.remainingInitialInput.length > 0
    ) {
      // Re-queue prior initial items until the tracker confirms they were delivered to the API.
      inputItems.push(...this.remainingInitialInput);
      for (const item of this.remainingInitialInput) {
        this.registerPreparedItemSource(item);
      }
    }

    for (const item of generatedItems) {
      if (item.type === 'tool_approval_item') {
        continue;
      }
      const rawItem = item.rawItem;
      if (!rawItem || typeof rawItem !== 'object') {
        continue;
      }
      if (this.sentItems.has(rawItem) || this.serverItems.has(rawItem)) {
        continue;
      }
      if (
        this.serverItemKeys.has(
          getServerComparableItemKey(rawItem as AgentInputItem),
        )
      ) {
        continue;
      }
      generatedItemsForInput.push(item);
    }

    const preparedGeneratedItems = extractOutputItemsFromRunItems(
      generatedItemsForInput,
      this.reasoningItemIdPolicy,
    );
    for (const [index, preparedItem] of preparedGeneratedItems.entries()) {
      const sourceItem = generatedItemsForInput[index]?.rawItem as
        | AgentInputItem
        | undefined;
      this.registerPreparedItemSource(preparedItem, sourceItem);
    }
    inputItems.push(...preparedGeneratedItems);
    const filteredSupplementalItems = filterSupplementalGeneratedItems(
      supplementalGeneratedItems,
      preparedGeneratedItems,
      this.sentItems,
      this.serverItems,
      this.serverItemKeys,
    );
    for (const item of filteredSupplementalItems) {
      this.registerPreparedItemSource(item);
    }
    inputItems.push(...filteredSupplementalItems);

    return inputItems;
  }

  /**
   * Marks the provided originals as delivered so future turns do not resend them and any
   * pending initial inputs can be dropped once the server acknowledges receipt.
   */
  markInputAsSent(
    items: (AgentInputItem | undefined)[],
    options?: { filterApplied?: boolean; allTurnItems?: AgentInputItem[] },
  ) {
    const delivered = new Set<AgentInputItem>();
    const dropRemainingInitialInput = options?.filterApplied ?? false;
    const markFilteredItemsAsSent =
      options?.filterApplied && Boolean(options.allTurnItems);

    this.addDeliveredItems(delivered, items);

    const allTurnItems = options?.allTurnItems;
    if (markFilteredItemsAsSent && allTurnItems) {
      this.addDeliveredItems(delivered, allTurnItems);
    }

    this.updateRemainingInitialInput(
      delivered,
      Boolean(dropRemainingInitialInput),
    );
  }

  private addDeliveredItems(
    delivered: Set<AgentInputItem>,
    items: (AgentInputItem | undefined)[],
  ) {
    for (const item of items) {
      if (!item || typeof item !== 'object') {
        continue;
      }
      const sourceItem = this.resolvePreparedItemSource(item);
      if (!sourceItem || typeof sourceItem !== 'object') {
        continue;
      }
      if (delivered.has(sourceItem)) {
        continue;
      }
      // Some inputs may be repeated in the filtered list; only mark unique originals once.
      delivered.add(sourceItem);
      this.sentItems.add(sourceItem);
    }
  }

  private registerPreparedItemSource(
    preparedItem: AgentInputItem,
    sourceItem?: AgentInputItem,
  ) {
    if (!preparedItem || typeof preparedItem !== 'object') {
      return;
    }
    if (!sourceItem || typeof sourceItem !== 'object') {
      this.preparedItemSources.set(preparedItem, preparedItem);
      return;
    }
    this.preparedItemSources.set(preparedItem, sourceItem);
  }

  private trackServerItem(item: AgentInputItem) {
    this.serverItems.add(item);
    this.serverItemKeys.add(getServerComparableItemKey(item));
  }

  private isServerAcknowledgedItem(item: AgentInputItem): boolean {
    return (
      this.serverItems.has(item) ||
      this.serverItemKeys.has(getServerComparableItemKey(item))
    );
  }

  private resolvePreparedItemSource(item: AgentInputItem): AgentInputItem {
    if (!item || typeof item !== 'object') {
      return item;
    }
    return this.preparedItemSources.get(item) ?? item;
  }

  private updateRemainingInitialInput(
    delivered: Set<AgentInputItem>,
    dropRemainingInitialInput: boolean,
  ) {
    if (
      !this.remainingInitialInput ||
      this.remainingInitialInput.length === 0 ||
      delivered.size === 0
    ) {
      if (dropRemainingInitialInput && this.remainingInitialInput) {
        this.remainingInitialInput = null;
      }
      return;
    }

    this.remainingInitialInput = this.remainingInitialInput.filter(
      (item) => !delivered.has(item),
    );
    if (this.remainingInitialInput.length === 0) {
      this.remainingInitialInput = null;
    } else if (dropRemainingInitialInput) {
      this.remainingInitialInput = null;
    }
  }
}

function filterSupplementalGeneratedItems(
  supplementalGeneratedItems: AgentInputItem[],
  preparedGeneratedItems: AgentInputItem[],
  sentItems: WeakSet<object>,
  serverItems: WeakSet<object>,
  serverItemKeys: ReadonlySet<string>,
): AgentInputItem[] {
  const preparedFunctionResultCallIds = new Set(
    preparedGeneratedItems.flatMap((item) => {
      if (
        !item ||
        typeof item !== 'object' ||
        item.type !== 'function_call_result' ||
        typeof item.callId !== 'string'
      ) {
        return [];
      }
      return [item.callId];
    }),
  );

  return supplementalGeneratedItems.filter((item) => {
    if (!item || typeof item !== 'object') {
      return true;
    }
    if (sentItems.has(item) || serverItems.has(item)) {
      return false;
    }
    if (serverItemKeys.has(getServerComparableItemKey(item))) {
      return false;
    }
    if (
      item.type !== 'function_call_result' ||
      typeof item.callId !== 'string'
    ) {
      return true;
    }
    return !preparedFunctionResultCallIds.has(item.callId);
  });
}

function getServerComparableItemKey(item: AgentInputItem): string {
  if (!item || typeof item !== 'object') {
    return getAgentInputItemKey(item);
  }

  const record = structuredClone(item) as Record<string, unknown>;
  const providerData = isRecord(record.providerData)
    ? { ...record.providerData }
    : undefined;
  const providerItemId =
    providerData && typeof providerData.providerItemId === 'string'
      ? providerData.providerItemId
      : undefined;

  if (providerData) {
    delete providerData.providerItemId;
    if (Object.keys(providerData).length > 0) {
      record.providerData = providerData;
    } else {
      delete record.providerData;
    }
  }

  if (providerItemId) {
    record.id = providerItemId;
  }

  return getAgentInputItemKey(record as AgentInputItem);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Result of applying a `callModelInputFilter`.
 * - `modelInput` is the payload that goes to the model.
 * - `sourceItems` maps each filtered item back to the original turn item (or `undefined` when none).
 *   This lets the conversation tracker know which originals reached the model.
 * - `persistedItems` are the filtered clones we should commit to session memory so the stored
 *   history reflects any redactions or truncation introduced by the filter.
 * - `filterApplied` signals whether a filter ran so callers can distinguish empty filtered results
 *   from the filter being skipped entirely.
 */
export type FilterApplicationResult = {
  modelInput: { input: AgentInputItem[]; instructions?: string };
  sourceItems: (AgentInputItem | undefined)[];
  persistedItems: AgentInputItem[];
  filterApplied: boolean;
};

/**
 * Applies the optional callModelInputFilter and returns the filtered input alongside the original
 * items so downstream tracking and session persistence stay in sync with what the model saw.
 */
export async function applyCallModelInputFilter<TContext>(
  agent: Agent<TContext, AgentOutputType>,
  callModelInputFilter: CallModelInputFilter<any> | undefined,
  context: RunContext<TContext>,
  inputItems: AgentInputItem[],
  systemInstructions: string | undefined,
): Promise<FilterApplicationResult> {
  const cloneInputItems = (
    items: AgentInputItem[],
    map?: WeakMap<object, AgentInputItem>,
  ) =>
    items.map((item) => {
      const cloned = structuredClone(item) as AgentInputItem;
      if (map && cloned && typeof cloned === 'object') {
        map.set(cloned as object, item);
      }
      return cloned;
    });

  // Record the relationship between the cloned array passed to filters and the original inputs.
  const cloneMap = new WeakMap<object, AgentInputItem>();
  const originalPool = buildAgentInputPool(inputItems);
  const fallbackOriginals: AgentInputItem[] = [];
  // Track any original object inputs so filtered replacements can still mark them as delivered.
  for (const item of inputItems) {
    if (item && typeof item === 'object') {
      fallbackOriginals.push(item);
    }
  }
  const removeFromFallback = (candidate: AgentInputItem | undefined) => {
    if (!candidate || typeof candidate !== 'object') {
      return;
    }
    const index = fallbackOriginals.findIndex(
      (original) => original === candidate,
    );
    if (index !== -1) {
      fallbackOriginals.splice(index, 1);
    }
  };
  const takeFallbackOriginal = (): AgentInputItem | undefined => {
    const next = fallbackOriginals.shift();
    if (next) {
      removeAgentInputFromPool(originalPool, next);
    }
    return next;
  };

  // Always create a deep copy so downstream mutations inside filters cannot affect
  // the cached turn state.
  const clonedBaseInput = cloneInputItems(inputItems, cloneMap);
  const base = {
    input: clonedBaseInput,
    instructions: systemInstructions,
  };
  if (!callModelInputFilter) {
    return {
      modelInput: base,
      sourceItems: [...inputItems],
      persistedItems: [],
      filterApplied: false,
    };
  }

  try {
    const result = await callModelInputFilter({
      modelData: base,
      agent,
      context: context.context,
    } as CallModelInputFilterArgs<any>);

    if (!result || !Array.isArray(result.input)) {
      throw new UserError(
        'callModelInputFilter must return a ModelInputData object with an input array.',
      );
    }

    // Preserve a pointer to the original object backing each filtered clone so downstream
    // trackers can keep their bookkeeping consistent even after redaction.
    const sourceItems = result.input.map((item) => {
      if (!item || typeof item !== 'object') {
        return undefined;
      }
      const original = cloneMap.get(item as object);
      if (original) {
        removeFromFallback(original);
        removeAgentInputFromPool(originalPool, original);
        return original;
      }
      const key = getAgentInputItemKey(item as AgentInputItem);
      const matchedByContent = takeAgentInputFromPool(originalPool, key);
      if (matchedByContent) {
        removeFromFallback(matchedByContent);
        return matchedByContent;
      }
      const fallback = takeFallbackOriginal();
      if (fallback) {
        return fallback;
      }
      return undefined;
    });

    const clonedFilteredInput = cloneInputItems(result.input);
    return {
      modelInput: {
        input: clonedFilteredInput,
        instructions:
          typeof result.instructions === 'undefined'
            ? systemInstructions
            : result.instructions,
      },
      sourceItems,
      persistedItems: clonedFilteredInput.map((item) => structuredClone(item)),
      filterApplied: true,
    };
  } catch (error) {
    addErrorToCurrentSpan({
      message: 'Error in callModelInputFilter',
      data: { error: String(error) },
    });
    throw error;
  }
}
